import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert, IconButton, Snackbar, Switch, FormControlLabel,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import api from '../../api/axios';

const EMPTY = { name: '', address: '', phone: '', email: '', is_main: false };

function BranchDialog({ branch, open, onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!branch?.id;

  useEffect(() => {
    setForm(branch ? { name: branch.name, address: branch.address || '', phone: branch.phone || '', email: branch.email || '', is_main: !!branch.is_main } : EMPTY);
    setError('');
  }, [branch, open]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handle = async () => {
    if (!form.name) { setError('Branch name is required.'); return; }
    setLoading(true); setError('');
    try {
      if (isEdit) await api.put(`/branches/${branch.id}`, form);
      else await api.post('/branches', form);
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save branch');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Branch' : 'New Branch'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Branch Name" value={form.name} onChange={set('name')} fullWidth required />
        <TextField label="Address" value={form.address} onChange={set('address')} fullWidth multiline rows={2} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Phone" value={form.phone} onChange={set('phone')} fullWidth />
          <TextField label="Email" value={form.email} onChange={set('email')} fullWidth type="email" />
        </Box>
        <FormControlLabel
          control={<Switch checked={form.is_main} onChange={e => setForm(f => ({ ...f, is_main: e.target.checked }))} />}
          label="Main Branch"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handle} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [snack, setSnack] = useState('');

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/branches'); setBranches(data.data || []); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this branch? This cannot be undone.')) return;
    try {
      await api.delete(`/branches/${id}`);
      setSnack('Branch deleted'); load();
    } catch (e) { setSnack(e.response?.data?.message || 'Delete failed'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Branch Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setSelected(null); setDialogOpen(true); }}>Add Branch</Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Branch Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branches.map(b => (
                  <TableRow key={b.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{b.name}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{b.address || '—'}</Typography></TableCell>
                    <TableCell>{b.phone || '—'}</TableCell>
                    <TableCell>{b.email || '—'}</TableCell>
                    <TableCell>
                      {b.is_main ? <Chip label="Main" size="small" color="primary" /> : <Chip label="Branch" size="small" variant="outlined" />}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => { setSelected(b); setDialogOpen(true); }}><Edit fontSize="small" /></IconButton>
                      {!b.is_main && <IconButton size="small" color="error" onClick={() => handleDelete(b.id)}><Delete fontSize="small" /></IconButton>}
                    </TableCell>
                  </TableRow>
                ))}
                {branches.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No branches found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <BranchDialog
        branch={selected} open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => { setDialogOpen(false); setSnack(selected ? 'Branch updated' : 'Branch created'); load(); }}
      />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
